import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-e6bb3dae"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "d-flex align-center ga-2" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", null, "Asset Lifecycle Costs", -1))
const _hoisted_3 = {
  class: "mt-2",
  style: {"height":"600px"}
}

import {
  AssetManagementPlanMaster,
  DateObject,
  FunctionalLocation,
  Tactic,
} from "@/classes";
import DateField from "@/components/base/inputs/DateField.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { computed, onMounted, ref } from "vue";
import { Line } from "vue-chartjs";
import NuxtAxiosInstance from "@/plugins/axios";
import Decimal from "decimal.js";
import { useListStore } from "@/store/lists";
import { format } from "date-fns";
import FLOCSearch from "@/components/asset-management-plan/FLOCSearch.vue";

type CostsAggregate = (typeof costsAggregates)[number];
type AssetLifecycleCosts = {
  workorders: Record<string, Decimal>;
  tactics: Record<string, Decimal>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AssetLifecycleCostsChart',
  setup(__props) {

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const $axios = NuxtAxiosInstance;

const lists = useListStore();
const ampMasters = lists.useList<AssetManagementPlanMaster>(
  "assetManagementPlanMasters"
);

const orderTypesList = ref(["PM01", "PM02", "PM03"]);
const orderTypes = ref<string[]>([]);
const tacticsSelected = ref<string[]>([]);

const costsAggregates = ["Cumulative", "Sum"] as const;
const start = ref<DateObject | null>(
  DateObject.add(new DateObject(), { years: -1 })
);
const end = ref<DateObject | null>(new DateObject());
const ampMasterIds = ref<string[]>([]);
const flocs = ref<FunctionalLocation[]>([]);
const aggregate = ref<CostsAggregate>("Cumulative");

const costs = ref<AssetLifecycleCosts>({
  workorders: {},
  tactics: {},
});

const tactics = ref<Tactic[]>([]);
const abortController = ref(new AbortController());
const fetchingCount = ref(0);

onMounted(() => getData());

const getData = async () => {
  try {
    abortController.value.abort();
    abortController.value = new AbortController();
    fetchingCount.value++;
    costs.value = await $axios.$post<AssetLifecycleCosts>(
      "/metrics/asset-lifecycle-costs",
      {
        start: start.value,
        end: end.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: flocs.value.map((floc) => floc.id),
        order_types: orderTypes.value,
        tactics: tacticsSelected.value,
      },
      {
        signal: abortController.value.signal,
      }
    );

    tactics.value = await $axios.$post<Tactic[]>(
      "/metrics/asset-lifecycle-costs/plans",
      {
        start: start.value,
        end: end.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: flocs.value.map((floc) => floc.id),
        order_types: orderTypes.value,
        tactics: [],
      },
      {
        signal: abortController.value.signal,
      }
    );
  } catch (e) {
    // Aborted
  } finally {
    fetchingCount.value--;
  }
};

const data = computed<ChartData<"line">>(() => {
  const dateSets = new Set<string>();
  Object.keys(costs.value.workorders).forEach((date) => {
    dateSets.add(date);
  });
  Object.keys(costs.value.tactics).forEach((date) => {
    dateSets.add(date);
  });

  const dates = Array.from(dateSets).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );
  const workorderCosts: number[] = [];
  const tacticCosts: number[] = [];
  let workorderSum = 0;
  let tacticSum = 0;
  for (const date of dates) {
    const workorderCost = costs.value.workorders[date] ?? new Decimal(0);
    const tacticCost = costs.value.tactics[date] ?? new Decimal(0);
    if (aggregate.value === "Cumulative") {
      workorderSum += workorderCost.toNumber();
      tacticSum += tacticCost.toNumber();
      workorderCosts.push(workorderSum);
      tacticCosts.push(tacticSum);
    } else {
      workorderCosts.push(workorderCost.toNumber());
      tacticCosts.push(tacticCost.toNumber());
    }
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Actual",
        data: workorderCosts,
        borderColor: "rgb(75, 192, 192)",
        fill: true,
        backgroundColor: "rgb(75, 192, 192, 0.2)",
        tension: 0,
      },
      {
        label: "Forecast",
        data: tacticCosts,
        borderColor: "rgb(245, 142, 47)",
        fill: true,
        backgroundColor: "rgb(245, 142, 47, 0.2)",
        tension: 0,
      },
    ],
  };
});

const options = computed<ChartOptions<"line">>(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "month",
        },
      },
      y: {
        min: 0,
        ticks: {
          callback: (value) => {
            return `$${value.toLocaleString()}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (items) => {
            const date = new Date(items[0].parsed.x);
            return format(date, "dd MMM yyyy");
          },
          label: (item) => {
            return `${item.dataset.label}: $${item.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
  };
});

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _hoisted_2,
      _createVNode(DateField, {
        class: "width-160",
        modelValue: start.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((start).value = $event)),
        label: "From",
        variant: "outlined",
        density: "compact",
        "hide-details": "",
        onChange: getData
      }, null, 8, ["modelValue"]),
      _createVNode(DateField, {
        class: "width-160",
        modelValue: end.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((end).value = $event)),
        label: "To",
        variant: "outlined",
        density: "compact",
        "hide-details": "",
        onChange: getData
      }, null, 8, ["modelValue"]),
      _createVNode(_component_v_autocomplete, {
        class: "width-160 compact-autocomplete",
        modelValue: ampMasterIds.value,
        "onUpdate:modelValue": [
          _cache[2] || (_cache[2] = ($event: any) => ((ampMasterIds).value = $event)),
          getData
        ],
        label: "AMP",
        items: _unref(ampMasters),
        "item-value": "id",
        "item-title": "name",
        multiple: "",
        variant: "outlined",
        density: "compact",
        width: "400px",
        "max-width": "400px",
        "hide-details": ""
      }, null, 8, ["modelValue", "items"]),
      _createVNode(FLOCSearch, {
        modelValue: flocs.value,
        "onUpdate:modelValue": [
          _cache[3] || (_cache[3] = ($event: any) => ((flocs).value = $event)),
          getData
        ],
        label: "FLOC",
        variant: "outlined",
        density: "compact",
        width: "400px",
        "max-width": "400px",
        "hide-details": "",
        multiple: ""
      }, null, 8, ["modelValue"]),
      _createVNode(_component_v_autocomplete, {
        class: "width-160 compact-autocomplete",
        modelValue: orderTypes.value,
        "onUpdate:modelValue": [
          _cache[4] || (_cache[4] = ($event: any) => ((orderTypes).value = $event)),
          getData
        ],
        label: "Order Type",
        items: orderTypesList.value,
        multiple: "",
        variant: "outlined",
        density: "compact",
        width: "400px",
        "max-width": "400px",
        "single-line": "",
        "hide-details": ""
      }, null, 8, ["modelValue", "items"]),
      _createVNode(_component_v_autocomplete, {
        class: "width-160 compact-autocomplete",
        modelValue: tacticsSelected.value,
        "onUpdate:modelValue": [
          _cache[5] || (_cache[5] = ($event: any) => ((tacticsSelected).value = $event)),
          getData
        ],
        label: "Tactic",
        items: tactics.value,
        "item-value": "id",
        "item-title": "task_list_header_description",
        multiple: "",
        variant: "outlined",
        density: "compact",
        width: "400px",
        "max-width": "400px",
        "single-line": "",
        "hide-details": ""
      }, null, 8, ["modelValue", "items"]),
      _createVNode(_component_v_radio_group, {
        modelValue: aggregate.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((aggregate).value = $event)),
        inline: "",
        "hide-details": ""
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(costsAggregates, (aggregate) => {
            return _createVNode(_component_v_radio, {
              key: aggregate,
              label: aggregate,
              value: aggregate
            }, null, 8, ["label", "value"])
          }), 64))
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_spacer),
      (fetchingCount.value > 0)
        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
            key: 0,
            indeterminate: "",
            size: 25
          }))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(Line), {
        data: data.value,
        options: options.value
      }, null, 8, ["data", "options"])
    ])
  ], 64))
}
}

})